import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { triggerApi, agentApi, type TriggerType } from '@/lib/api';
import { Radio, Plus, Circle, ChevronRight, Calendar, Zap, Webhook } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/toast';
import { PageGuide } from '@/components/PageGuide';

export const Route = createFileRoute('/_auth/triggers/')({ component: TriggersPage });

type TriggerTab = 'SCHEDULED' | 'EVENT' | 'WEBHOOK';

const TAB_META: Record<TriggerTab, { icon: React.ReactNode; label: string }> = {
  SCHEDULED: { icon: <Calendar size={13} />, label: 'Scheduled' },
  EVENT:     { icon: <Zap size={13} />,      label: 'Event Listeners' },
  WEBHOOK:   { icon: <Webhook size={13} />,  label: 'Webhooks' },
};

export default function TriggersPage() {
  const qc       = useQueryClient();

  const [activeTab, setActiveTab] = useState<TriggerTab>('SCHEDULED');
  const [showCreate, setShowCreate] = useState(false);

  const { data: triggers = [], isLoading } = useQuery({ queryKey: ['triggers'], queryFn: triggerApi.list });
  const { data: agents = [] }              = useQuery({ queryKey: ['agents'],   queryFn: agentApi.list });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => triggerApi.update(id, { active }),
    onSuccess: (_, vars) => { toast.success(vars.active ? 'Trigger activated' : 'Trigger deactivated'); qc.invalidateQueries({ queryKey: ['triggers'] }); },
  });

  const filtered = triggers.filter((t) => t.triggerType === activeTab);

  return (
    <div className="page">
      <PageGuide pageKey="triggers" />

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Triggers</h1>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Orchestrate agent executions via cron schedules, internal events, or webhook URLs</p>
        </div>
        <button
          id="new-trigger-btn"
          onClick={() => setShowCreate(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 12px var(--color-primary-glow)' }}
        >
          <Plus size={15} /> New Trigger
        </button>
      </div>

      {/* ── Type tabs ── */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #1a1f35', marginTop: 20 }}>
        {(Object.entries(TAB_META) as [TriggerTab, { icon: React.ReactNode; label: string }][]).map(([key, meta]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              color: activeTab === key ? 'var(--color-primary)' : '#475569',
              borderBottom: `2px solid ${activeTab === key ? 'var(--color-primary)' : 'transparent'}`,
              marginBottom: -1, whiteSpace: 'nowrap',
            }}
          >
            {meta.icon} {meta.label} ({triggers.filter((t) => t.triggerType === key).length})
          </button>
        ))}
      </div>

      {isLoading && <div style={{ padding: '40px 0', textAlign: 'center' }}><Spinner size="md" /></div>}

      {!isLoading && filtered.length === 0 && (
        <div style={{ padding: '56px 0', textAlign: 'center', border: '1px dashed #1e2130', borderRadius: 12, color: '#475569' }}>
          <Radio size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 14 }}>No {activeTab.toLowerCase()} triggers yet</p>
          <p style={{ margin: '6px 0 0', fontSize: 12, color: '#334155' }}>Create one to start automating agent executions.</p>
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {filtered.map((trig) => (
            <div key={trig.id} style={{ borderRadius: 14, border: '1px solid #1a1f35', background: '#0a0e1c', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#818cf8', background: '#6366f120', padding: '2px 9px', borderRadius: 20, border: '1px solid #6366f130', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {trig.triggerType}
                </span>
                <button
                  onClick={() => toggleMutation.mutate({ id: trig.id, active: !trig.active })}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid', borderColor: trig.active ? '#10b98140' : '#47556940', background: trig.active ? '#10b98120' : '#47556920', color: trig.active ? '#34d399' : '#64748b' }}
                >
                  <Circle size={6} fill="currentColor" /> {trig.active ? 'Active' : 'Inactive'}
                </button>
              </div>

              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>{trig.name}</h3>
              <p style={{ margin: 0, fontSize: 12, color: '#64748b', lineHeight: 1.5, flex: 1 }}>{trig.description ?? 'No description.'}</p>

              {/* config summary */}
              <div style={{ fontSize: 11, color: '#475569', background: '#0f1221', borderRadius: 8, padding: '7px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                {trig.triggerType === 'SCHEDULED' && <><Calendar size={11} /> Cron: <code style={{ color: '#94a3b8' }}>{(trig.config as any)?.cronExpression}</code></>}
                {trig.triggerType === 'EVENT'     && <><Zap size={11} /> Event: <code style={{ color: '#94a3b8' }}>{(trig.config as any)?.eventType}</code></>}
                {trig.triggerType === 'WEBHOOK'   && <><Webhook size={11} /> Path: <code style={{ color: '#94a3b8' }}>/webhooks/{(trig.config as any)?.endpointPath}</code></>}
              </div>

              <div style={{ display: 'flex', gap: 8, paddingTop: 10, borderTop: '1px solid #1a1f35', alignItems: 'center' }}>
                <span style={{ flex: 1, fontSize: 11, color: '#475569' }}>Agent: <span style={{ color: '#94a3b8' }}>{trig.agent?.name ?? 'Unknown'}</span></span>
                <Link
                  to="/triggers/$triggerId"
                  params={{ triggerId: trig.id }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', border: '1px solid #2d3148', borderRadius: 8, color: '#94a3b8', fontSize: 12, textDecoration: 'none' }}
                >
                  Configure <ChevronRight size={13} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateTriggerDrawer
          agents={agents}
          defaultType={activeTab}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['triggers'] });
            setShowCreate(false);
            toast.success('Trigger created successfully');
          }}
        />
      )}
    </div>
  );
}

// ── Create Drawer ─────────────────────────────────────────────────────────────

function CreateTriggerDrawer({ agents, defaultType, onClose, onCreated }: {
  agents: Array<{ id: string; name: string; status: string }>;
  defaultType: TriggerTab;
  onClose: () => void;
  onCreated: (trig: any) => void;
}) {
  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  const [agentId, setAgentId]         = useState(agents[0]?.id ?? '');
  const [triggerType, setTriggerType] = useState<TriggerType>(defaultType);
  const [config, setConfig] = useState<any>({
    cronExpression: '*/15 * * * *', timezone: 'Europe/Helsinki',
    initialMessage: 'Run scheduled check-in flow.', sourceApp: 'scheduler',
  });

  const { mutate, isPending } = useMutation({
    mutationFn: () => triggerApi.create({ name, description, agentId, triggerType, config }),
    onSuccess: onCreated,
    onError: (err: any) => {
      toast.error(err.message || 'Failed to create trigger');
    },
  });

  const handleTypeChange = (type: TriggerType) => {
    setTriggerType(type);
    const defaults: Record<TriggerType, any> = {
      SCHEDULED: { cronExpression: '*/15 * * * *', timezone: 'Europe/Helsinki', initialMessage: 'Run scheduled check-in flow.', sourceApp: 'scheduler' },
      EVENT:     { eventType: 'conversation.closed', filter: {}, initialMessage: 'Follow up on closed conversation.' },
      WEBHOOK:   { endpointPath: 'my-webhook', secret: '', messageTemplate: 'Webhook fired: {payload.message}' },
    };
    setConfig(defaults[type]);
  };

  const setC = (k: string, v: string) => setConfig((c: any) => ({ ...c, [k]: v }));

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 40 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 480, background: '#0d1022', borderLeft: '1px solid #1e2130', zIndex: 50, padding: '28px 24px', overflowY: 'auto', boxShadow: '-16px 0 60px rgba(0,0,0,0.6)', animation: 'drawerIn 0.22s cubic-bezier(0.34,1.2,0.64,1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#e2e8f0' }}>New Trigger</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>

        <Row label="Name" required><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Daily Customer Check-in" autoFocus style={inputStyle} /></Row>
        <Row label="AI Agent" required>
          <select value={agentId} onChange={(e) => setAgentId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="" disabled>Select agent…</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.status})</option>)}
          </select>
        </Row>
        <Row label="Description"><input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What does this trigger do?" style={inputStyle} /></Row>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Trigger Type</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['SCHEDULED', 'EVENT', 'WEBHOOK'] as const).map((t) => (
              <button key={t} onClick={() => handleTypeChange(t)} style={{ flex: 1, padding: '7px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: '1px solid', borderColor: triggerType === t ? 'var(--color-primary)' : '#1e2130', background: triggerType === t ? 'var(--color-primary-dim)' : 'transparent', color: triggerType === t ? 'var(--color-primary)' : '#64748b' }}>{t}</button>
            ))}
          </div>
        </div>

        {triggerType === 'SCHEDULED' && (<>
          <Row label="Cron Expression" required><input value={config.cronExpression} onChange={(e) => setC('cronExpression', e.target.value)} placeholder="*/15 * * * *" style={inputStyle} /></Row>
          <Row label="Timezone" required><input value={config.timezone} onChange={(e) => setC('timezone', e.target.value)} placeholder="Europe/Helsinki" style={inputStyle} /></Row>
          <Row label="Initial Message" required><textarea value={config.initialMessage} onChange={(e) => setC('initialMessage', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} /></Row>
        </>)}
        {triggerType === 'EVENT' && (<>
          <Row label="Event Type" required><input value={config.eventType} onChange={(e) => setC('eventType', e.target.value)} placeholder="conversation.closed" style={inputStyle} /></Row>
          <Row label="Initial Message" required><textarea value={config.initialMessage} onChange={(e) => setC('initialMessage', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} /></Row>
        </>)}
        {triggerType === 'WEBHOOK' && (<>
          <Row label="Endpoint Path" required><input value={config.endpointPath} onChange={(e) => setC('endpointPath', e.target.value)} placeholder="my-webhook" style={inputStyle} /></Row>
          <Row label="Message Template" required><textarea value={config.messageTemplate} onChange={(e) => setC('messageTemplate', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} /></Row>
        </>)}

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #1e2130', borderRadius: 9, color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => mutate()} disabled={!name.trim() || !agentId || isPending} style={{ flex: 2, padding: '10px', background: 'var(--color-primary)', border: 'none', borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!name.trim() || !agentId) ? 0.5 : 1 }}>
            {isPending ? <Spinner size="sm" /> : 'Create Trigger'}
          </button>
        </div>
      </div>
      <style>{`@keyframes drawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}

function Row({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>{label} {required && <span style={{ color: '#f87171' }}>*</span>}</label>
      {children}
    </div>
  );
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 6 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '9px 12px', background: '#0f1221', border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
